/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";

const SidebarContext = createContext();

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  // State to manage sidebar collapse - default to collapsed (closed)
  const [collapsed, setCollapsed] = useState(true);
  // Track if in mobile viewport; if true, don't render sidebar
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const [sidebarMode, setSidebarMode] = useState("navigation");
  const previousCollapsedRef = useRef(null);

  // Function to determine if the current view is considered "mobile"
  // We'll use Tailwind's 'md' breakpoint (768px) as the threshold.
  const isMobileView = useCallback(() => window.innerWidth < 768, []);

  // Check if user is logged in
  const isLoggedIn = useCallback(() => {
    return typeof window !== "undefined" && !!localStorage.getItem("token");
  }, []);

  // Effect to set initial collapsed state and handle window resizing
  useEffect(() => {
    // On login, always open the sidebar (set collapsed to false)
    if (isLoggedIn()) {
      setCollapsed(false);
    }

    // Keep sidebar collapsed by default on all screen sizes
    // Only force collapse on mobile resize, but start collapsed everywhere
    const initialIsMobile = isMobileView();
    setIsMobile(initialIsMobile);
    if (initialIsMobile) setCollapsed(true);

    // Event listener for window resize to update collapsed state
    const handleResize = () => {
      const isNowMobile = isMobileView();
      setIsMobile(isNowMobile);
      if (isNowMobile) setCollapsed(true); // Force collapse on mobile
    };

    window.addEventListener("resize", handleResize);

    // Listen for login events
    const handleLogin = () => {
      if (!isMobileView()) {
        setCollapsed(false);
      }
    };

    // Listen for custom login event
    window.addEventListener("userLoggedIn", handleLogin);

    // Cleanup function to remove the event listeners
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("userLoggedIn", handleLogin);
    };
  }, [isLoggedIn, isMobileView]);

  // Toggle collapse function
  // This function will only allow manual toggling on non-mobile views
  const toggleSidebar = useCallback(() => {
    if (sidebarMode === "history") {
      return;
    }
    if (!isMobileView()) {
      setCollapsed((prev) => !prev);
    }
  }, [isMobileView, sidebarMode]);

  // Function to open sidebar (useful for post-login)
  const openSidebar = useCallback(() => {
    if (!isMobileView()) {
      setCollapsed(false);
    }
  }, [isMobileView]);

  // Function to close sidebar
  const closeSidebar = useCallback(() => {
    setCollapsed(true);
  }, []);

  const openHistoryView = useCallback(() => {
    if (sidebarMode === "history") {
      return;
    }
    previousCollapsedRef.current = collapsed;
    if (collapsed) {
      setCollapsed(false);
    }
    setSidebarMode("history");
  }, [collapsed, sidebarMode]);

  const closeHistoryView = useCallback(() => {
    if (sidebarMode !== "history") {
      return;
    }
    setSidebarMode("navigation");
    if (previousCollapsedRef.current !== null) {
      setCollapsed(previousCollapsedRef.current);
    }
    previousCollapsedRef.current = null;
  }, [sidebarMode]);

  const resetSidebarState = useCallback(() => {
    previousCollapsedRef.current = null;
    setSidebarMode("navigation");
    setCollapsed(true);
    setIsMobile(isMobileView());
  }, [isMobileView]);

  const value = {
    collapsed,
    setCollapsed,
    isMobile,
    setIsMobile,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    isMobileView,
    sidebarMode,
    openHistoryView,
    closeHistoryView,
    resetSidebarState,
  };

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};
